import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { useAuth } from '../auth/AuthProvider.js';
import { homePathForRole } from '../auth/guards.js';

interface LocationState {
  from?: string;
  /** Set by the reset-password page so a completed reset is confirmed on arrival. */
  notice?: string;
}

export function LoginPage(): ReactElement {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const notice = (location.state as LocationState | null)?.notice;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login({ email, password });
      // A deep link the guard bounced away from wins; otherwise land on the role's home.
      const from = (location.state as LocationState | null)?.from;
      navigate(from ?? homePathForRole(user.role), { replace: true });
    } catch (cause) {
      setError(messageForError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-session-text">Sign in</h1>
        <p className="mt-1 mb-6 text-sm text-session-muted">
          Use the email address your interview invitation was sent to.
        </p>

        <form onSubmit={onSubmit} className="panel space-y-4 p-5" noValidate>
          {notice ? (
            <p role="status" className="form-notice">
              {notice}
            </p>
          ) : null}

          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="email" className="field-label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="password" className="field-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="field-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-session-muted underline">
              Forgot your password?
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
